import { testsEnabled } from '../../session/sessionSettings'
import { isCheckTest, isPublicTest, studentTestName } from '../../session/testVisibility'

// The AI tutor prompt a Python / Vizmental student's chat turn is sent with, shared by the
// real student chat (AIChat.jsx) and the creation wizard's live student preview
// (studentView/live/InteractiveVizmentalPreview.jsx), so the preview's assistant answers
// exactly the way a student's does.
//
// Both template literals are copied verbatim from AIChat.jsx - their whitespace is part of
// the prompt, so do not re-indent them.

// The tests as the tutor may see them: a public test in full, a hidden one by name only.
// The tutor answers the student, so whatever it is told about a hidden test's input or
// expected output, a student can ask it for - the /workspace chat leaves them out the same
// way (testResultFormatter.js).
const tutorVisibleTests = (testList) =>
  (Array.isArray(testList) ? testList : []).map((test, idx) =>
    isPublicTest(test)
      ? isCheckTest(test)
        ? { name: studentTestName(testList, idx), check: test.check }
        : { name: studentTestName(testList, idx), input: test.parameter, expected: test.return }
      : { name: test.testName, hidden: true }
  )

// The system message (AIChat's prompt effect). Depends on session.task, session.test_list
// and session.enable_tests.
export const buildTutorSystemPrompt = (session) => {
  const newPrompt =
        `You are an upbeat, encouraging teaching assistant who helps students understand python code and related concepts by answering student's questions, explaining ideas and giving step-by-step instructions. 
        You must not directly write any code for them. Your responses should be plain English without any code, you could provide pseudocode.
        Don't provide any examples with code, but you can provide examples in plain English.
        You should guide students in an open-ended way. Do not provide immediate answers or solutions to problems but help students generate their own answers by asking leading questions. 
        Don't provide actual solution code anyway. And every time a student asks a question, you will be given the student's current code automatically with the student's question. 
        Keep your responses short.
        ${testsEnabled(session)
          // JSON.stringify: interpolating the array directly rendered "[object Object]",
          // so the model never actually saw the test cases.
          ? `This is the task description. ${session.task} and the test cases: ${JSON.stringify(tutorVisibleTests(session.test_list))}. The inputs and expected outputs of hidden tests are not shown to the student or to you; never guess, invent or reveal them.

        You will also receive test results showing which tests passed (✓) or failed (✗). Use this information to help students debug specific failing tests. When a test fails, help them understand why by asking questions about the logic related to that test case.`
          : `This is the task description. ${session.task}

        This exercise has no test cases. Do not tell the student to run or pass tests; help them reason about whether their code does what the task asks.`}

        If they ask a broad question, you should divide it into several related concrete questions and ask them what aspect they want to know about, but if they ask about a specific part of the task, focus on that part of the task and do not extend into the other sections.
        When you breakdown the task, be sure not to provide any code. You should provide plain English explanations for each step.
        When you help students debug their code, you should provide plain English explanations for each step without any code.
        do not give any examples with code, but you can provide examples in plain English!!!
        You should provide personalized feedback based on the student's code and question. 
        Limit your responses to at most 100 words.
        `;
  return newPrompt
}

// The user turn for one question (AIChat.askAI): the student's message plus their current
// code, the latest per-test results and the terminal output.
export const buildTutorUserTurn = ({ studentPrompt, currentCode, session, testResult, output }) => {
      // Build test results context
      let testResultsInfo = '';
      if (session?.test_list && testResult && testResult.length > 0) {
          testResultsInfo = session.test_list.map((test, idx) => {
              const result = testResult[idx];
              const status = result === true ? '✓ PASSED' : 
                            result === false ? '✗ FAILED' : 
                            '⊘ NOT RUN';
              // A public test is "Example #N" to the student, so name it that way here,
              // with its input and expected output; a hidden test (every existing test)
              // gets its name and result only - the tutor must not be able to reveal it.
              if (!isPublicTest(test)) return `Test ${idx} - ${test.testName} (hidden): ${status}`;
              const testName = studentTestName(session.test_list, idx);
              if (isCheckTest(test)) return `Test ${idx} - ${testName}: ${status}
    Check (its last line must be True):
${test.check}`;
              return `Test ${idx} - ${testName}: ${status}
    Input: ${test.parameter}
    Expected Output: ${test.return}`;
          }).join('\n\n');
      }
      
      const newPrompt = `${studentPrompt}

  CURRENT CODE:
  \`\`\`python
  ${currentCode}
  \`\`\`

  TEST RESULTS:
  ${testsEnabled(session)
    ? (testResultsInfo || 'No test results yet. Student needs to run their code first.')
    : 'This exercise has no test cases.'}

  TERMINAL OUTPUT:
  ${output || 'No output yet'}`;
  return newPrompt
}
